import { useState } from 'react';
import { formatDateTime } from '../lib/format.js';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import CodeIcon from '@mui/icons-material/Code';
import TerminalIcon from '@mui/icons-material/Terminal';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import RepeatIcon from '@mui/icons-material/Repeat';
import PeopleIcon from '@mui/icons-material/PeopleAltOutlined';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase';
import { KIND_COLOR, type ErrorKind } from './errorShared.js';
import { IconStat } from '../components/RowStats.js';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';
import { useActiveProject } from '../lib/activeProject.js';

/** Small glyph per error kind, echoing the reference small-icon rows. */
function kindIcon(kind: ErrorKind) {
  if (kind === 'CONSOLE') return <TerminalIcon />;
  if (kind === 'NETWORK') return <CloudOffIcon />;
  if (kind === 'CRASH') return <PhonelinkEraseIcon />;
  return <CodeIcon />; // JS / default
}

interface ErrorGroup {
  id: string;
  projectName: string;
  kind: ErrorKind;
  status: string;
  message: string;
  count: number;
  firstSeenAt: string;
  lastSeenAt: string;
  affectedSessions: number;
  affectedUsers: number;
  impactScore: number;
  severity: 'critical' | 'high' | 'medium' | 'low';
  impactReasons: string[];
}
interface Report {
  summary: { totalGroups: number; openGroups: number; totalOccurrences: number; byKind: Record<string, number> };
  groups: ErrorGroup[];
}

/** Severity is the first thing read on the row, so it gets the strong colours. */
const SEVERITY_COLOR: Record<ErrorGroup['severity'], string> = {
  critical: '#e11d48',
  high: '#f5a623',
  medium: '#6366f1',
  low: '#64748b',
};

const KIND_TABS = [
  { key: '', label: 'all' },
  { key: 'JS', label: 'js' },
  { key: 'CONSOLE', label: 'console' },
  { key: 'NETWORK', label: 'network' },
] as const;

export function ErrorsPage() {
  const all = useT();
  const t = all.signals.errors;
  const navigate = useNavigate();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const { selected: projectId } = useActiveProject(orgId);
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');

  const report = useQuery({
    enabled: !!orgId,
    queryKey: ['errors', orgId, kind, search, projectId],
    queryFn: async () => {
      const p = new URLSearchParams({ limit: '200' });
      if (kind) p.set('kind', kind);
      if (search) p.set('search', search);
      if (projectId) p.set('projectId', projectId);
      return (await api.get(`/v1/orgs/${orgId}/errors?${p}`)).data as Report;
    },
  });

  const s = report.data?.summary;
  const sort = useSort<'impact' | 'error' | 'kind' | 'project' | 'people' | 'events' | 'lastSeen'>('impact');
  const rows = sort.sorted(report.data?.groups ?? [], {
    impact: (g) => g.impactScore,
    error: (g) => g.message,
    kind: (g) => g.kind,
    project: (g) => g.projectName,
    people: (g) => g.affectedUsers,
    events: (g) => g.count,
    lastSeen: (g) => g.lastSeenAt,
  });

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <BugReportIcon sx={{ color: '#ef4444' }} />
        <Typography variant="h5">{t.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t.subtitle}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <Kpi label={t.kpi.groups} value={s?.totalGroups ?? 0} />
        <Kpi label={t.kpi.open} value={s?.openGroups ?? 0} accent="#ef4444" />
        <Kpi label={t.kpi.occurrences} value={s?.totalOccurrences ?? 0} />
        <Kpi label={t.kpi.byKind} value={`${s?.byKind.NETWORK ?? 0} / ${s?.byKind.JS ?? 0} / ${s?.byKind.CONSOLE ?? 0}`} />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {KIND_TABS.map((tab) => (
          <Chip
            key={tab.key}
            label={t.tabs[tab.label]}
            onClick={() => setKind(tab.key)}
            color={kind === tab.key ? 'primary' : 'default'}
            variant={kind === tab.key ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600 }}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder={t.searchPlaceholder} value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: 260 }} />
      </Stack>

      <Paper sx={{ overflow: 'hidden' }}>
        {report.isLoading && <LinearProgress />}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {sort.head('impact', t.columns.impact)}
                {sort.head('error', t.columns.error)}
                {sort.head('kind', t.columns.kind)}
                {sort.head('project', t.columns.project)}
                {sort.head('people', t.columns.people, 'right')}
                {sort.head('events', t.columns.events, 'right')}
                {sort.head('lastSeen', t.columns.lastSeen)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((g) => (
                <TableRow key={g.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/errors/${g.id}`)}>
                  <TableCell>
                    <Tooltip title={g.impactReasons.join(' · ')}>
                      <Chip
                        size="small"
                        label={t.severity[g.severity] ?? g.severity}
                        sx={{
                          bgcolor: `${SEVERITY_COLOR[g.severity]}22`,
                          color: SEVERITY_COLOR[g.severity],
                          fontWeight: 700,
                          textTransform: 'capitalize',
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 520 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Box sx={{ color: KIND_COLOR[g.kind], mt: '2px', display: 'grid', placeItems: 'center', '& svg': { fontSize: 18 } }}>
                        {kindIcon(g.kind)}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }} noWrap>
                          {g.message}
                        </Typography>
                        {g.status !== 'OPEN' && (
                          <Chip size="small" label={g.status.toLowerCase()} sx={{ mt: 0.5, height: 18, fontSize: 10 }} />
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={g.kind} sx={{ bgcolor: `${KIND_COLOR[g.kind]}22`, color: KIND_COLOR[g.kind], fontWeight: 700 }} />
                  </TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{g.projectName}</Typography></TableCell>
                  <TableCell align="right">
                    <IconStat
                      icon={<PeopleIcon />}
                      value={g.affectedUsers}
                      color={SEVERITY_COLOR[g.severity]}
                      title={t.peopleAcross(g.affectedUsers, g.affectedSessions)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconStat icon={<RepeatIcon />} value={g.count} color={KIND_COLOR[g.kind]} title={t.occurrences} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{formatDateTime(g.lastSeenAt)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
              {!report.isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      {kind || search ? t.emptyFiltered : t.empty}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}

function Kpi({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={700} sx={{ color: accent }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}
